/**
 * Componente AppointmentList
 * Lista de citas con paginación y filtros. Orden por defecto: más recientes primero (scheduled_at desc).
 * @module components/appointments/AppointmentList
 */

import React, { useMemo } from 'react';
import { AppointmentCard } from './AppointmentCard';
import { useAppointments } from '@/hooks/useAppointments';
import type { AppointmentListParams, Appointment } from '@/types/appointment.types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import { CalendarIcon } from '@heroicons/react/24/outline';

interface AppointmentListProps {
  filters?: AppointmentListParams;
  onAppointmentClick?: (appointmentId: string) => void;
  onEditClick?: (appointmentId: string) => void;
  onDeleteClick?: (appointmentId: string) => void;
  className?: string;
}

export const AppointmentList: React.FC<AppointmentListProps> = ({
  filters,
  onAppointmentClick,
  onEditClick,
  onDeleteClick,
  className = '',
}) => {
  const params = useMemo<AppointmentListParams>(
    () => ({
      limit: 50,
      sort_by: 'scheduled_at',
      sort_order: 'desc',
      ...filters,
    }),
    [
      filters?.delegation_id,
      filters?.opportunity_id,
      filters?.contact_id,
      filters?.agent_id,
      filters?.start_date,
      filters?.end_date,
      filters?.modality,
      filters?.sort_by,
      filters?.sort_order,
    ]
  );

  const { appointments, isLoading, error } = useAppointments(params);

  const sortedAppointments = useMemo(() => {
    if (!appointments || appointments.length === 0) return [];
    const by = params.sort_by ?? 'scheduled_at';
    const order = params.sort_order ?? 'desc';
    return [...appointments].sort((a: Appointment, b: Appointment) => {
      const aVal = by === 'scheduled_at' ? new Date(a.scheduled_at).getTime() : new Date(a.created_at).getTime();
      const bVal = by === 'scheduled_at' ? new Date(b.scheduled_at).getTime() : new Date(b.created_at).getTime();
      return order === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [appointments, params.sort_by, params.sort_order]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={<CalendarIcon className="w-12 h-12" />}
        title="Error al cargar citas"
        description="No se pudieron cargar las citas. Por favor, intenta nuevamente."
      />
    );
  }

  if (!appointments || appointments.length === 0) {
    return (
      <EmptyState
        icon={<CalendarIcon className="w-12 h-12" />}
        title="No hay citas"
        description="No se encontraron citas con los filtros actuales."
      />
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {sortedAppointments.map((appointment) => (
        <AppointmentCard
          key={appointment.id}
          appointment={appointment}
          onClick={
            onAppointmentClick
              ? () => onAppointmentClick(appointment.id)
              : undefined
          }
          onEdit={
            onEditClick ? () => onEditClick(appointment.id) : undefined
          }
          onDelete={
            onDeleteClick ? () => onDeleteClick(appointment.id) : undefined
          }
        />
      ))}
    </div>
  );
};
